/**
 * StatusTransitionToNbModal — confirms the Awaiting Signature →
 * Signed & Sent to New Business transition by generating the new
 * business handover memo PDF for Vicky.
 *
 * Flow:
 *   1. User clicks "Signed & Sent to New Business" on the file detail
 *      view — instead of running the status change directly, the parent
 *      opens this modal.
 *   2. The modal shows an auto-filled summary (read-only) of the memo
 *      header + numbered case descriptions, plus two notes textareas:
 *      "Adviser notes" — seeded from the file's Summary (the Client File
 *      Brief in files.written_instructions), editable by whoever is
 *      generating the memo; edits feed the memo ONLY and are never
 *      written back to the file record (restored 29 September 2026; it
 *      was first built on 24 July 2026 and removed on 4 September 2026
 *      when the form's Additional Notes field went) — and
 *      "Notes for New Business" — the Administration Assistant's message
 *      to Vicky at the moment of handover. It always starts EMPTY (it is
 *      never seeded from written_instructions or anything else) and is
 *      editable only while the active role is Administration Assistant.
 *      Once the memo has been generated the value is saved to
 *      files.handover_notes and locked (files.handover_notes_locked_at);
 *      from then on the field renders read-only for every role and any
 *      regeneration reuses the locked value without prompting.
 *      The field is NOT a file note: it never creates a note row,
 *      illuminates a card, routes to an owner or appears in the login
 *      catch-up overlay. Later communication goes through the notepad.
 *   3. Clicking "Generate PDF & complete" runs the supplied
 *      `onConfirm({ adviserNotes, handoverNotes, ... })` callback. The parent fetches
 *      the notepad history and generates the PDF first; only after that
 *      succeeds does it save + lock the notes and update the status, in
 *      one write. A failure at any step keeps the modal open, the file
 *      in Awaiting Signature, and the notes unsaved and editable here
 *      (this component's local state survives, so a retry keeps the
 *      text the user typed).
 *   4. Cancel closes the modal with no state change.
 *
 * The `cases` prop is an array of free-text case descriptions
 * (strings) — each entry is what the adviser wrote on the submission
 * form. Line breaks are preserved as-is.
 */

import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, FileText, Loader, AlertTriangle } from 'lucide-react';
import { formatDate, formatDateTime } from '../../utils/dateHelpers';

/**
 * Format the splits as a single line for the read-only summary.
 *   "M Lourens — 70%  /  Optimum PFP — 30%"
 */
function formatSplitsLine(splits) {
  if (!Array.isArray(splits) || splits.length === 0) return '—';
  return splits
    .map((row) => `${(row.name || '').trim()} — ${row.percent}%`)
    .join('  /  ');
}

export default function StatusTransitionToNbModal({
  file,
  commissionSplits,
  cases,
  role = null,
  busy = false,
  errorMessage = null,
  onCancel,
  onConfirm,
}) {
  // "Notes for New Business" — locked once the memo has been generated.
  // While locked the stored value is shown read-only and reused; before
  // that it starts EMPTY (never seeded from anything) and is editable
  // only for the Administration Assistant (app-level role gate, §30).
  const locked = Boolean(file?.handoverNotesLockedAt);
  const canEditNotes = !locked && role === 'assistant';
  const [handoverNotes, setHandoverNotes] = useState('');
  const lockedNotes = (file?.handoverNotes || '').trim();
  // Adviser notes — a LOCAL copy of the file's Summary (the Client File
  // Brief, files.written_instructions). Edits here shape the memo's Notes
  // card only and are NEVER persisted back to the file record. Line breaks
  // are preserved through to the PDF.
  const [adviserNotes, setAdviserNotes] = useState(file?.writtenInstructions || '');

  const today = new Date();
  const signed = file?.signedAt ? new Date(file.signedAt) : today;

  const handleConfirm = () => {
    if (busy) return;
    onConfirm({
      adviserNotes: adviserNotes.trim(),
      handoverNotes: locked ? lockedNotes : (canEditNotes ? handoverNotes.trim() : ''),
      dateSigned: signed,
      dateSubmittedToNb: today,
    });
  };

  const handleBackdropClick = () => {
    if (busy) return;
    onCancel();
  };

  const caseList = Array.isArray(cases) ? cases : [];

  return (
    <div style={styles.overlay} onClick={handleBackdropClick}>
      <motion.div
        className="card"
        style={styles.modal}
        onClick={(e) => e.stopPropagation()}
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
      >
        {/* Header strip */}
        <div style={styles.header}>
          <div>
            <div style={styles.caption}>Stage transition</div>
            <h2 style={styles.title}>Generate handover memo for Vicky</h2>
          </div>
          <div style={styles.transitionPills}>
            <span className="badge status-sent_to_sign">Awaiting signature</span>
            <ArrowRight size={14} color="#6B7A8D" />
            <span className="badge status-signed">Signed &amp; sent to NB</span>
          </div>
        </div>
        <p style={styles.subtitle}>
          Review the auto-filled details{canEditNotes ? ', add a note for Vicky if needed,' : ''} then generate the PDF to complete the transition.
        </p>

        {errorMessage && (
          <div style={styles.errorBox}>
            <AlertTriangle size={14} />
            <span style={{ flex: 1 }}>{errorMessage}</span>
          </div>
        )}

        {/* Section 1 — Memo header (auto-filled, read-only) */}
        <div style={styles.section}>
          <h3 style={styles.h2}>Memo header</h3>
          <Row label="Client name" value={file?.clientName || '—'} />
          <Row label="Date signed" value={formatDate(signed)} />
          <Row label="Date submitted to NB" value={formatDate(today)} />
          <Row label="Adviser commission split" value={formatSplitsLine(commissionSplits)} />
        </div>

        {/* Section 2 — Cases on file (auto-filled, read-only).
            Numbered list of free-text case descriptions, exactly as
            the adviser entered them on the file. Line breaks within
            a description are preserved (white-space: pre-wrap). */}
        <div style={styles.section}>
          <h3 style={styles.h2}>Cases on file — {caseList.length}</h3>
          {caseList.length === 0 ? (
            <div style={styles.emptyCases}>No cases recorded on this file.</div>
          ) : (
            <ol style={styles.caseList}>
              {caseList.map((description, idx) => (
                <li key={idx} style={styles.caseItem}>
                  <span style={styles.caseNumber}>{idx + 1}.</span>
                  <span style={styles.caseDescription}>
                    {description && description.trim()
                      ? description
                      : <em style={styles.caseMissing}>No description</em>}
                  </span>
                </li>
              ))}
            </ol>
          )}
        </div>

        {/* Section 3 — Adviser notes: the file's Summary (Client File
            Brief), seeded from written_instructions and editable for the
            memo only. Never written back to the file. */}
        <div style={styles.section}>
          <h3 style={styles.h2}>
            Adviser notes <span style={styles.optional}>(from the file's Summary)</span>
          </h3>
          <p style={styles.fieldHelper}>
            Printed on the memo under Notes. Edit freely — changes shape the memo only and are never saved to the file.
          </p>
          <textarea
            value={adviserNotes}
            onChange={(e) => setAdviserNotes(e.target.value)}
            placeholder="No Summary was written on this file."
            rows={8}
            style={styles.adviserNotes}
            disabled={busy}
          />
        </div>

        {/* Section 4 — Notes for New Business. One optional field, owned
            by the Administration Assistant at handover. Never seeded.
            Locked (read-only for every role) once the memo has been
            generated; hidden entirely for a non-editing role when there
            is nothing to show. */}
        {canEditNotes && (
          <div style={styles.section}>
            <h3 style={styles.h2}>
              Notes for New Business <span style={styles.optional}>(optional)</span>
            </h3>
            <p style={styles.fieldHelper}>
              Printed on the memo for Vicky. Saved and locked when the PDF is generated — anything later goes in the notepad.
            </p>
            <textarea
              value={handoverNotes}
              onChange={(e) => setHandoverNotes(e.target.value)}
              placeholder="Anything Vicky should know before she starts. Optional."
              rows={4}
              style={styles.notes}
              disabled={busy}
            />
          </div>
        )}
        {!canEditNotes && lockedNotes && (
          <div style={styles.section}>
            <h3 style={styles.h2}>
              Notes for New Business <span style={styles.optional}>(locked)</span>
            </h3>
            <p style={styles.fieldHelper}>
              {locked
                ? `Locked ${formatDateTime(file.handoverNotesLockedAt)} — reused on the memo as written.`
                : 'Read-only.'}
            </p>
            <div style={styles.notesLocked}>{lockedNotes}</div>
          </div>
        )}

        {/* Footer bar */}
        <div style={styles.footer}>
          <span style={styles.footerHint}>
            PDF will download · email to Vicky coming soon
          </span>
          <div style={styles.footerButtons}>
            <button
              type="button"
              className="btn btn-outline"
              onClick={onCancel}
              disabled={busy}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleConfirm}
              disabled={busy}
            >
              {busy ? (
                <>
                  <Loader size={14} style={{ animation: 'spin 1s linear infinite' }} /> Generating…
                </>
              ) : (
                <>
                  <FileText size={14} /> Generate PDF &amp; complete
                </>
              )}
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div style={styles.row}>
      <span style={styles.rowLabel}>{label}</span>
      <span style={styles.rowValue}>{value}</span>
    </div>
  );
}

const styles = {
  overlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0, 0, 0, 0.4)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '1rem',
  },
  modal: {
    maxWidth: '640px',
    width: '100%',
    padding: '1.5rem',
    maxHeight: '90vh',
    overflowY: 'auto',
  },
  header: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: '1rem',
    flexWrap: 'wrap',
  },
  caption: {
    fontSize: '0.7rem',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    color: 'var(--cp-text-muted)',
    marginBottom: '0.25rem',
  },
  title: {
    fontSize: '1.25rem',
    fontWeight: 600,
    color: 'var(--cp-text)',
    margin: 0,
  },
  transitionPills: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.4rem',
  },
  subtitle: {
    color: 'var(--cp-text-muted)',
    fontSize: '0.85rem',
    marginTop: '0.5rem',
    marginBottom: '1.25rem',
  },
  section: {
    marginBottom: '1.25rem',
  },
  h2: {
    fontSize: '0.85rem',
    fontWeight: 600,
    color: 'var(--cp-text)',
    marginBottom: '0.5rem',
    paddingBottom: '0.35rem',
    borderBottom: '1px solid var(--cp-surface-alt)',
  },
  optional: {
    fontWeight: 400,
    color: 'var(--cp-text-muted)',
    fontSize: '0.8rem',
  },
  fieldHelper: {
    fontSize: '0.78rem',
    color: 'var(--cp-text-muted)',
    margin: '0 0 0.5rem',
    lineHeight: 1.45,
  },
  row: {
    display: 'flex',
    alignItems: 'baseline',
    gap: '0.75rem',
    fontSize: '0.85rem',
    padding: '0.3rem 0',
  },
  rowLabel: {
    color: 'var(--cp-text-muted)',
    fontWeight: 500,
    flexShrink: 0,
    minWidth: '8.5rem',
  },
  rowValue: {
    color: 'var(--cp-text)',
    flex: 1,
    minWidth: 0,
  },
  emptyCases: {
    fontSize: '0.85rem',
    color: 'var(--cp-text-muted)',
    fontStyle: 'italic',
    padding: '0.5rem 0',
  },
  caseList: {
    listStyle: 'none',
    margin: 0,
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '0.7rem',
  },
  caseItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '0.55rem',
    fontSize: '0.88rem',
    lineHeight: 1.45,
  },
  caseNumber: {
    flexShrink: 0,
    minWidth: '1.5rem',
    fontWeight: 600,
    color: 'var(--cp-primary-dark)',
    fontVariantNumeric: 'tabular-nums',
  },
  caseDescription: {
    flex: 1,
    minWidth: 0,
    color: 'var(--cp-text)',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  caseMissing: {
    color: '#9FB1C2',
    fontStyle: 'italic',
  },
  notes: {
    width: '100%',
    padding: '0.6rem 0.75rem',
    border: '1.5px solid var(--cp-border)',
    borderRadius: '8px',
    fontSize: '0.85rem',
    resize: 'vertical',
    fontFamily: 'inherit',
    boxSizing: 'border-box',
  },
  adviserNotes: {
    width: '100%',
    padding: '0.6rem 0.75rem',
    border: '1.5px solid var(--cp-border)',
    borderRadius: '8px',
    fontSize: '0.85rem',
    lineHeight: 1.5,
    resize: 'vertical',
    fontFamily: 'inherit',
    boxSizing: 'border-box',
    whiteSpace: 'pre-wrap',
  },
  notesLocked: {
    width: '100%',
    padding: '0.6rem 0.75rem',
    border: '1.5px solid var(--cp-border)',
    borderRadius: '8px',
    fontSize: '0.85rem',
    fontFamily: 'inherit',
    boxSizing: 'border-box',
    whiteSpace: 'pre-wrap',
    background: 'var(--cp-bg)',
    color: 'var(--cp-text)',
  },
  errorBox: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '0.6rem 0.85rem',
    marginBottom: '1rem',
    background: 'rgba(192, 57, 43, 0.08)',
    border: '1px solid rgba(192, 57, 43, 0.2)',
    borderRadius: '8px',
    color: 'var(--cp-danger)',
    fontSize: '0.82rem',
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '0.75rem',
    flexWrap: 'wrap',
    paddingTop: '0.75rem',
    borderTop: '1px solid var(--cp-surface-alt)',
  },
  footerHint: {
    fontSize: '0.78rem',
    color: 'var(--cp-text-muted)',
    fontStyle: 'italic',
  },
  footerButtons: {
    display: 'flex',
    gap: '0.5rem',
  },
};
